export interface NavItem { label: string; to: string; params?: Record<string, string>; locked?: boolean; }
export interface NavGroup { label: string; items: NavItem[]; }

/** Every guided workflow other than Guided Packaging is locked for the MVP. */
const wf = (label: string, code: string): NavItem => ({ label, to: "/workflows/$code", params: { code }, locked: true });
const assessment: NavItem = { label: "Guided Assessment", to: "/workflows/assessment", locked: true };
const packaging: NavItem = { label: "Guided Packaging", to: "/workflows/guided-packaging" };
const assistant: NavItem = { label: "Oxley Assistant", to: "/ask" };
/** The landing page: one chat across every product of the catalog. The Agents group carries only this for now. */
export const AGENT_ROCKET: NavItem = { label: "Agent Rocket", to: "/agents/agent-rocket" };
export const HOME_PATH = AGENT_ROCKET.to;

/** Left rail: guided workflows in the order of the client's design canvas. Every group is always visible. */
export const NAV_GROUPS: NavGroup[] = [
  { label: "Agents", items: [AGENT_ROCKET] },
  { label: "Assess", items: [assessment, wf("Guided Market Research", "market-research")] },
  {
    label: "Stand up the offer",
    items: [wf("NPI", "npi"), packaging, wf("Guided Metric Design", "metric-design"), wf("Guided Segmentation", "segmentation")],
  },
  { label: "Set price", items: [wf("Guided Price Setting", "price-setting"), wf("Guided Price Increase", "price-increase")] },
  { label: "Get price & govern", items: [wf("Guided Discount Authority", "discount-authority"), wf("Guided Governance Setup", "governance-setup")] },
  {
    label: "Enable & grow",
    items: [wf("Guided Sales Enablement", "sales-enablement"), wf("Guided Migration", "migration"), wf("Guided Renewals & Expansion", "renewals"), wf("Guided Realization Review", "realization"), wf("Guided ROI Model", "roi")],
  },
];

/** Bottom of the rail, under the workflows. */
export const UTILITY_NAV: NavItem[] = [
  { label: "Workflow Designer", to: "/workflow-designer", locked: true },
  { label: "Connections", to: "/connections", locked: true },
];

/** Account menu, opened from the person's name at the foot of the rail. */
export const BOTTOM_NAV: NavItem[] = [
  { label: "Governance", to: "/governance", locked: true },
  { label: "Insights", to: "/insights", locked: true },
  { label: "Assistant skills", to: "/skills", locked: true },
  { label: "Oxley management", to: "/admin" },
  { label: "Feedback", to: "/feedback" },
];

/** Top rail of the Packaging Workspace: Market · Catalog · Packaging · Prices · Discounts · Enablement · KB. */
export const TOP_TABS: NavItem[] = [
  { label: "Market", to: "/market", locked: true },
  { label: "Catalog", to: "/catalog" },
  { label: "Packaging", to: "/packaging" },
  { label: "Prices", to: "/prices", locked: true },
  { label: "Discounts", to: "/discounts", locked: true },
  { label: "Enablement", to: "/enablement", locked: true },
  { label: "KB", to: "/kb", locked: true },
];

/**
 * Top rail = product areas. Left rail = guided workflows only.
 * Each area lists the workflows that belong to it, used by area pages and by the step runner.
 */
export interface AreaNav { label: string; workflows: NavItem[]; note?: string; }
export const AREA_NAV: Record<string, AreaNav> = {
  "/market": { label: "Market", workflows: [assessment, wf("Guided Market Research", "market-research")] },
  "/catalog": { label: "Catalog", workflows: [packaging, wf("NPI", "npi")] },
  "/packaging": { label: "Packaging", workflows: [packaging, wf("Guided Metric Design", "metric-design"), wf("Guided Segmentation", "segmentation")] },
  "/prices": { label: "Prices", workflows: [wf("Guided Price Setting", "price-setting"), wf("Guided Price Increase", "price-increase")] },
  "/discounts": { label: "Discounts", workflows: [wf("Guided Discount Authority", "discount-authority")] },
  "/enablement": { label: "Enablement", workflows: [wf("Guided Sales Enablement", "sales-enablement"), wf("Guided Migration", "migration"), wf("Guided Renewals & Expansion", "renewals")] },
  "/kb": { label: "KB", workflows: [assistant], note: "Knowledge lookup is coming in a later release." },
  "/insights": { label: "Insights", workflows: [wf("Guided Realization Review", "realization"), wf("Guided ROI Model", "roi")] },
  "/governance": { label: "Governance", workflows: [wf("Guided Governance Setup", "governance-setup")] },
};

/** Which top area a page belongs to. Guided Packaging belongs to Packaging. */
export function areaFor(pathname: string): AreaNav | null {
  if (pathname.startsWith("/workflows/guided-packaging")) return AREA_NAV["/packaging"];
  const key = Object.keys(AREA_NAV).find((k) => pathname === k || pathname.startsWith(`${k}/`));
  if (key) return AREA_NAV[key];
  if (pathname.startsWith("/workflows/")) {
    const code = pathname.split("/")[2];
    const hit = Object.values(AREA_NAV).find((a) => a.workflows.some((i) => i.params?.code === code || i.to === pathname));
    return hit ?? null;
  }
  return null;
}
